import { useCallback, useMemo, useState } from 'react';
import {
  filterVisibleItems,
  legendToggleStatusMessage,
  pruneHiddenKeys,
  toggleLegendVisibility,
} from './legendToggle';

export {
  filterVisibleItems,
  legendToggleStatusMessage,
  pruneHiddenKeys,
  toggleLegendVisibility,
};

const EMPTY_LABELS = Object.freeze({});

/**
 * Shared legend toggle state for multi-series charts.
 *
 * @param {string[]} itemKeys — stable series/segment keys for this chart instance
 * @param {{ minVisible?: number, labels?: Record<string, string> }} [options]
 *   `labels` maps series key → human-readable label for aria-live announcements.
 */
export function useLegendToggle(itemKeys, options = {}) {
  const { minVisible = 1, labels: labelsOption } = options;
  const labels = labelsOption || EMPTY_LABELS;
  const keys = useMemo(
    () => (itemKeys || []).filter((k) => k != null && k !== ''),
    // Serialize so callers can pass inline arrays without resetting toggle state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(itemKeys) ? itemKeys.join('\0') : '']
  );

  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [statusMessage, setStatusMessage] = useState('');

  const activeHidden = useMemo(
    () => pruneHiddenKeys(hiddenKeys, keys),
    [hiddenKeys, keys]
  );

  const isVisible = useCallback(
    (key) => !activeHidden.has(key),
    [activeHidden]
  );

  const toggle = useCallback(
    (key) => {
      setHiddenKeys((prev) => {
        const pruned = pruneHiddenKeys(prev, keys);
        const { nextHidden, changed, blocked } = toggleLegendVisibility(
          pruned,
          key,
          keys,
          minVisible
        );
        if (blocked) {
          setStatusMessage('At least one series must remain visible');
        } else if (changed) {
          const nowHidden = nextHidden.has(key);
          setStatusMessage(legendToggleStatusMessage(key, nowHidden, labels));
        }
        return changed ? nextHidden : pruned === prev ? prev : pruned;
      });
    },
    [keys, minVisible, labels]
  );

  const visibleKeys = useMemo(
    () => keys.filter((k) => !activeHidden.has(k)),
    [keys, activeHidden]
  );

  return {
    hiddenKeys: activeHidden,
    visibleKeys,
    isVisible,
    toggle,
    statusMessage,
    minVisible,
  };
}
